import { Button, Row, Tag, Tooltip, Typography, notification } from 'antd';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import { EyeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import {
  FilePdfOutlined,
  FileAddOutlined,
  FileMarkdownOutlined,
} from '@ant-design/icons';
import Table from '../../../../components/general/table';
import { ExportExcelProps } from '../../../../helpers/excel';
// import ContractsForm from '../detail';
import moment from 'moment';
import { ExportPdfProps } from '../../../../helpers/pdf';
import HistoricBalanceForm from '../detail';
import { Invoice } from '../../../../api/models/invoices';
import { useEffect, useState } from 'react';
import { formatMoney } from '../../../../utils/general';
import { get } from '../../../../api';
import { CanShow } from '../../../../components';
import { useUser } from '../../../../hooks';

const label = 'factura';
const scrollTable = 1500;

type GenerateTableColumnProps = {
  getColumnSearchProps: (propName: string) => ColumnType<Invoice>;
  showModal: (type: string, id: number | null) => void;
  handleDelete: (id: number) => void;
};

const InvoicesList = (props: any) => {
  const { customerSelected, customerName } = props;
  const { Text } = Typography;
  const [route, setRoute] = useState('/invoices');
  const [extendedRoute, setExtendedRoute] = useState(
    `/customers/${customerSelected}/invoices`
  );
  const titlePdf = `Reporte de facturación ${customerName} : ${customerSelected}`;
  const { user } = useUser();

  useEffect(() => {
    setExtendedRoute(`/customers/${customerSelected}/invoices`);
  }, [customerSelected]);

  const openPreviewFile = async (invoice: any, fileType: string) => {
    let fileResponse = await get<any>(
      `/invoices/${invoice.id}/${fileType}`
    ).then((e) => e);
    if (fileResponse.success) {
      window.open(fileResponse.data.url, '_blank', 'noreferrer');
    } else {
      notification['error']({
        message: `${fileResponse.message}`,
      });
    }
  };

  const generateTableColumns: (
    props: GenerateTableColumnProps
  ) => ColumnsType<Invoice> = (props) => {
    const { getColumnSearchProps, showModal, handleDelete } = props;
    return [
      {
        title: '# Legal',
        width: 150,
        dataIndex: 'legal_number',
        key: 'legal_number',
        ...getColumnSearchProps('legal_number'),
      },
      {
        title: 'Fecha emisión',
        key: 'issue_date',
        dataIndex: 'issue_date',
        render: (issue_date) => (
          <>{moment.tz(issue_date, 'UTC').format('DD/MM/YYYY')}</>
        ),
      },
      {
        title: 'OV',
        dataIndex: 'order_id',
        key: 'order_id',
        ...getColumnSearchProps('order_id'),
      },
      {
        title: 'Estatus',
        dataIndex: 'status_id',
        key: 'status_id',
        filters: [
          { text: 'Pagada', value: 1 },
          {
            text: 'No Pagada',
            value: 2,
          },
          {
            text: 'Parcialmente Pagada',
            value: 3,
          },
        ],
        onFilter: (value, record) => record?.status_id === value,
        // ...getColumnSearchProps('status_id'),
        render: (status_id) => (
          <Text> {status_id === 0 ? 'Pagada' : 'No pagada'}</Text>
        ),
      },
      {
        title: 'Moneda',
        dataIndex: 'currency',
        key: 'currency',
        ...getColumnSearchProps('currency'),
      },
      {
        title: 'Monto',
        dataIndex: 'total',
        key: 'total',
        ...getColumnSearchProps('total'),
        render: (total) => <Text>$ {formatMoney(total)}</Text>,
      },
      {
        title: 'Documento',
        dataIndex: 'document_type_id',
        key: 'document_type_id',
        ...getColumnSearchProps('document_type_id'),
        render: (document_type_id) => (
          <Text> {document_type_id === 0 ? 'Factura' : 'Nota de crédito'}</Text>
        ),
      },
      {
        title: 'Agente',
        dataIndex: 'sales_agent',
        key: 'sales_agent',
        ...getColumnSearchProps('sales_agent'),
      },
      {
        title: 'Archivos',
        dataIndex: 'id',
        key: 'id',
        align: 'center',
        render: (id, record) => (
          <>
            <Tooltip title="Xml">
              <Tag color={'green'}>
                <FileMarkdownOutlined
                  onClick={() => openPreviewFile(record, 'xml')}
                />
              </Tag>
            </Tooltip>

            <Tooltip title="PDF">
              <Tag color={'red'}>
                <FilePdfOutlined
                  onClick={() => openPreviewFile(record, 'pdf')}
                />
              </Tag>
            </Tooltip>
          </>
        ),
      },
      {
        title: 'Acciones',
        key: 'sup',
        fixed: user?.role !== 'Customer' ? 'right' : undefined,
        width: 160,
        align: 'center',
        render: (user) => (
          <CanShow roles={['Admin', 'Financial']}>
            <>
              <Tooltip title="Ver más información">
                <Button
                  type="primary"
                  ghost
                  shape="default"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => showModal('watch', user.id)}
                />
              </Tooltip>
              <Tooltip title="Archivos">
                <Button
                  type="primary"
                  style={{
                    marginLeft: 10,
                    backgroundColor: '#7b96ac',
                    borderColor: '#7b96ac',
                  }}
                  shape="default"
                  size="small"
                  icon={<FileAddOutlined />}
                  onClick={() => showModal('files', user.id)}
                />
              </Tooltip>
              <Tooltip title="Editar">
                <Button
                  style={{
                    marginLeft: 10,
                    backgroundColor: '#1b4289',
                    borderColor: '#1b4289',
                  }}
                  type="primary"
                  shape="default"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => showModal('edit', user.id)}
                />
              </Tooltip>
              <Tooltip title="Eliminar">
                <Button
                  style={{
                    marginLeft: 10,
                    backgroundColor: '#e63241',
                    borderColor: '#cf1322',
                  }}
                  type="primary"
                  shape="default"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(user.id)}
                />
              </Tooltip>
            </>
          </CanShow>
        ),
      },
    ];
  };

  const generateExportExcelProps: () => ExportExcelProps = () => ({
    fileName: 'Reporte_Facturación',
    sheetName: 'Facturación',
    tableName: `Tabla de facturación ${customerName} : ${customerSelected}`,
    columnNames: [
      // { label: '# Folio', key: 'invcHead_MXFiscalFolio' },
      {
        label: '# Legal',
        key: 'legal_number',
      },
      {
        label: 'Fecha emisión',
        key: 'issue_date',
        format: (val) => moment.tz(val, 'UTC').format('DD/MM/YYYY'),
      },
      // {
      //   label: 'Fecha vencimiento',
      //   key: 'invcHead_DueDate',
      //   format: (val) => moment(val).format('DD/MM/YYYY'),
      // },
      { label: 'OV', key: 'order_id' },
      {
        label: 'Estatus',
        key: 'status_id',
        format: (val) => {
          switch (val) {
            case 1:
              return 'Pagada';
            case 2:
              return 'No Pagada';
            case 3:
              return 'Parcialmente Pagada';
            default:
              return 'Desconocido';
          }
        },
      },
      {
        label: 'Moneda',
        key: 'currency',
      },
      // {
      //   label: 'Subtotal',
      //   key: 'calculated_SubTotal',
      //   format: (val) => `$${formatMoney(val)}`,
      // },
      // {
      //   label: 'Total',
      //   key: 'invcHead_DocInvoiceAmt',
      //   format: (val) => `$${formatMoney(val)}`,
      // },
      {
        label: 'Monto',
        key: 'total',
        // format: (val) =>
        //   val.toLocaleString('es-ES', {
        //     minimumFractionDigits: 2,
        //     maximumFractionDigits: 2,
        //   }),
      },
      // {
      //   label: 'Impuestos',
      //   key: 'calculated_Impuestos',
      //   format: (val) => `$${formatMoney(val)}`,
      // },
      {
        label: 'Documento',
        key: 'document_type_id',
        format: (val) => (val === false ? 'Factura' : 'Nota de crédito'),
      },
      { label: 'Agente', key: 'sales_agent' },
      // { label: 'Días', key: 'terms_Description' },
    ],
  });

  const pdfColumns: ExportPdfProps[] = [
    // {
    //   header: '# Folio',
    //   key: 'invcHead_MXFiscalFolio',
    // },
    { header: '# Legal', key: 'legal_number' },
    {
      header: 'Fecha emisión',
      key: 'issue_date',
      format: (val) => moment.tz(val, 'UTC').format('DD/MM/YYYY'),
    },
    // {
    //   header: 'Fecha vencimiento',
    //   key: 'invcHead_DueDate',
    //   format: (val) => moment(val).format('DD/MM/YYYY'),
    // },
    { header: 'OV', key: 'order_id' },
    {
      header: 'Estatus',
      key: 'status_id',
      format: (val) => {
        switch (val) {
          case 1:
            return 'Pagada';
          case 2:
            return 'No Pagada';
          case 3:
            return 'Parcialmente Pagada';
          default:
            return 'Desconocido';
        }
      },
    },
    {
      header: 'Moneda',
      key: 'currency',
    },
    // {
    //   header: 'Subtotal',
    //   key: 'calculated_SubTotal',
    //   format: (val) => `$${formatMoney(val)}`,
    // },
    // {
    //   header: 'Total',
    //   key: 'invcHead_DocInvoiceAmt',
    //   format: (val) => `$${formatMoney(val)}`,
    // },
    {
      header: 'Monto',
      key: 'total',
      format: (val) => `$${formatMoney(val)}`,

      // format: (val) => `$${formatMoney(val)}`,
    },
    // {
    //   header: 'Impuestos',
    //   key: 'calculated_Impuestos',
    //   format: (val) => `$${formatMoney(val)}`,
    // },
    {
      header: 'Documento',
      key: 'document_type_id',
      format: (val) => (val === false ? 'Factura' : 'Nota de crédito'),
    },
    { header: 'Agente', key: 'sales_agent' },
    // { header: 'Días', key: 'terms_Description' },
  ];

  const invoiceTableProps = {
    label,
    route,
    extendedRoute,
    generateTableColumns,
    generateExportExcelProps,
    detailForm: HistoricBalanceForm,
    pdfColumns,
    titlePdf,
    scrollTable,
  };
  const InvoicesTable = Table<Invoice>(invoiceTableProps);

  return InvoicesTable;
};

export default InvoicesList;
